import log from "loglevel";
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import throttle from "lodash.throttle";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";
import { InputBox } from "@twilio-paste/core/input-box";
import { TextArea } from "@twilio-paste/core/textarea";
import { Button } from "@twilio-paste/core/button";
import { SendIcon } from "@twilio-paste/icons/esm/SendIcon";
import { SendMediaOptions } from "@twilio/conversations";
import { ProductVideoIcon } from "@twilio-paste/icons/esm/ProductVideoIcon";

import { AppState } from "../store/definitions";
import { AttachFileButton } from "./AttachFileButton";
import { FilePreview } from "./FilePreview";
import { detachFiles } from "../store/actions/genericActions";
import { CHAR_LIMIT } from "../constants";
import {
    formStyles,
    innerInputStyles,
    messageOptionContainerStyles,
    filePreviewContainerStyles,
    textAreaContainerStyles
} from "./styles/MessageInput.styles";
import contactBackend, { fileUpload } from "../utils/contactBackend";
import { getStoredSessionData } from "../sessionDataHandler";

export const MessageInput = () => {
    const dispatch = useDispatch();
    const [text, setText] = useState("");
    const [isSending, setIsSending] = useState(false);
    const conversationSid = useSelector((state: AppState) => state.session.conversationSid) || {};
    const { conversation, attachedFiles, fileAttachmentConfig } = useSelector((state: AppState) => ({
        conversation: state.chat.conversation,
        attachedFiles: state.chat.attachedFiles || [],
        fileAttachmentConfig: state.config.fileAttachment
    }));
    const oldAttachmentsLength = useRef((attachedFiles || []).length);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const attachmentsBoxRef = useRef<HTMLDivElement>(null);

    const throttleChange = useMemo(
        () =>
            throttle(() => {
                conversation?.typing();

                // in case the input was already focused, let's make sure to send the `read` status if the customer is typing
                if (conversation?.lastReadMessageIndex !== conversation?.lastMessage?.index) {
                    conversation?.setAllMessagesRead();
                }
            }, 500),
        [conversation]
    );

    const isSubmitDisabled = (!text.trim() && !attachedFiles?.length) || isSending;

    const handleVideo = async () => {
        if (!conversation) {
            log.error("Failed sending message: no conversation found");
            return;
        }
        let preparedMessage = conversation.prepareMessage();
        preparedMessage = preparedMessage.setBody("申请Video");
        await preparedMessage.build().send();
    };

    const send = async () => {
        if (isSubmitDisabled) {
            return;
        }
        if (!conversation) {
            log.error("Failed sending message: no conversation found");
            return;
        }
        setIsSending(true);
        console.log("attachedFiles", attachedFiles);
        if (attachedFiles.length) {
            const [file] = attachedFiles;
            const formData = new FormData();
            formData.append("json", JSON.stringify({ title: file.name, description: "" }));
            formData.append("fileData", file);

            const res = await fileUpload<{ contentDocument: Record<string, any>; contentVersion: Record<string, any> }>(
                formData
            );
            console.log("res", res);

            if (res?.contentDocument?.id) {
                // const res2 = await contactBackend("/bind", {
                //     contentDocumentId: res?.contentDocument?.id,
                //     conversationId: conversationSid,
                //     contentVersionId: res?.contentVersion?.id
                // });
                console.log("res2");
                console.log("conversationSid", conversationSid);
                const res3 = await contactBackend("/send", {
                    text,
                    filename: file.name,
                    filesize: file.size,
                    filetype: file.type,
                    contentDocumentId: res?.contentDocument?.id,
                    conversationId: conversationSid,
                    contentVersionId: res?.contentVersion?.id,
                    ...getStoredSessionData()
                });
                console.log("res3", res3);
            }
        } else {
            let preparedMessage = conversation.prepareMessage();
            preparedMessage = preparedMessage.setBody(text);
            await preparedMessage.build().send();
        }

        setText("");
        dispatch(detachFiles(attachedFiles));
        setIsSending(false);
        textAreaRef.current?.focus();
    };

    

    const onKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            (e.target as HTMLInputElement).form?.dispatchEvent(
                new Event("submit", { cancelable: true, bubbles: true })
            );
        }
    };

    const onChange = (val: ChangeEvent<HTMLTextAreaElement>) => {
        setText(val.target.value);

        throttleChange();
    };

    const onFocus = () => {
        conversation?.setAllMessagesRead();
    };

    useEffect(() => {
        textAreaRef.current?.setAttribute("rows", "1");
        // iOS 对小于 16px 的输入框聚焦会放大页面，导致发送按钮被挤出
        if (textAreaRef.current) {
            textAreaRef.current.style.fontSize = "16px";
            textAreaRef.current.style.maxWidth = "100%";
            textAreaRef.current.style.width = "100%";
            textAreaRef.current.style.boxSizing = "border-box";
        }
        textAreaRef.current?.focus();
    }, [textAreaRef]);

    // Ensuring attached files are automatically scrolled to
    useEffect(() => {
        if (!attachmentsBoxRef.current) {
            return;
        }

        if (attachedFiles.length > oldAttachmentsLength.current) {
            (attachmentsBoxRef.current.lastChild as Element)?.scrollIntoView();
        }

        oldAttachmentsLength.current = attachedFiles.length;
    }, [attachedFiles]);

    return (
        <Box
            as="form"
            {...formStyles}
            onSubmit={(e) => {
                e.preventDefault();
                send();
            }}
        >
            <InputBox element="MESSAGE_INPUT_BOX" disabled={isSending}>
                <Box as="div" {...innerInputStyles}>
                    <Box {...textAreaContainerStyles}>
                        <TextArea
                            ref={textAreaRef}
                            data-test="message-input-textarea"
                            placeholder="Type your message"
                            value={text}
                            element="MESSAGE_INPUT"
                            onChange={onChange}
                            onFocus={onFocus}
                            readOnly={isSending}
                            onKeyPress={onKeyPress}
                            maxLength={CHAR_LIMIT}
                        />
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center" flexShrink={0} flexGrow={0}>
                        <Box {...messageOptionContainerStyles}>
                            <Button
                                data-test="message-video"
                                variant="primary_icon"
                                size="icon_small"
                                aria-disabled={isSubmitDisabled}
                                onClick={handleVideo}
                            >
                                <ProductVideoIcon decorative={false} title="申请视频通话" size="sizeIcon30" />
                            </Button>
                        </Box>

                        <Box {...messageOptionContainerStyles}>
                            {fileAttachmentConfig?.enabled && <AttachFileButton textAreaRef={textAreaRef} />}
                        </Box>
                        <Box {...messageOptionContainerStyles}>
                            <Button
                                data-test="message-send-button"
                                variant="primary_icon"
                                size="icon_small"
                                type="submit"
                                aria-disabled={isSubmitDisabled}
                            >
                                <SendIcon decorative={false} title="Send message" size="sizeIcon30" />
                            </Button>
                        </Box>
                    </Box>
                </Box>
                {attachedFiles && (
                    <Box data-test="message-attachments" {...filePreviewContainerStyles} ref={attachmentsBoxRef}>
                        {attachedFiles.map((file, index) => (
                            <FilePreview
                                focusable={true}
                                key={index}
                                file={file}
                                isBubble={false}
                                disabled={isSending}
                            />
                        ))}
                    </Box>
                )}
            </InputBox>
        </Box>
    );
};
